import { useMemo } from 'react';
import { useApiQuery } from './useApiQuery';
import { partnerKeysApi } from '@/api/partnerKeys';
import type { PartnerKey, PartnerKeyListResponse } from '@/api/partnerKeys';
import mockData from '@/data/mock-data.json';

interface UsePartnerKeysOptions {
  enabled?: boolean;
}

export const usePartnerKeys = (partnerId: string | undefined, options: UsePartnerKeysOptions = {}) => {
  const { enabled = true } = options;

  const mockKeys = useMemo<PartnerKeyListResponse>(() => ({
    keys: (mockData.partnerKeys as PartnerKey[]).filter((k) => k.partnerId === partnerId),
  }), [partnerId]);

  const { data, isLoading, error, isUsingMockData, reload } = useApiQuery<PartnerKeyListResponse>(
    {
      queryFn: () => partnerKeysApi.list(partnerId as string),
      mockData: mockKeys,
      enabled: enabled && Boolean(partnerId),
    },
    [partnerId],
  );

  return { keys: data?.keys ?? [], isLoading, error, isUsingMockData, reload };
};

// Cross-partner "expiring soon" list for the Keyring landing page badge/table.
export const useExpiringKeys = (days = 30, options: UsePartnerKeysOptions = {}) => {
  const { enabled = true } = options;

  const mockKeys = useMemo<PartnerKeyListResponse>(() => {
    const cutoff = Date.now() + days * 86_400_000;
    return {
      keys: (mockData.partnerKeys as PartnerKey[]).filter(
        (k) => k.isActive && k.expiresAt && new Date(k.expiresAt).getTime() <= cutoff,
      ),
    };
  }, [days]);

  const { data, isLoading, error, isUsingMockData, reload } = useApiQuery<PartnerKeyListResponse>(
    {
      queryFn: () => partnerKeysApi.expiring(days),
      mockData: mockKeys,
      enabled,
    },
    [days],
  );

  return { keys: data?.keys ?? [], isLoading, error, isUsingMockData, reload };
};
